import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Button,
  Checkbox,
  DatePicker,
  Descriptions,
  Form,
  Input,

  Radio,
  Space,
  Spin,
  Typography,
  Tag,
  Upload,
  message,
} from 'antd';
import { DeleteOutlined, EyeOutlined, UploadOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import type { Dayjs } from 'dayjs';
import {
  RECEIVABLE_TYPE_LABEL,
  billingApi,
  type OfflinePaymentChannelCode,
  type OfflinePaymentContext,
  type OfflinePaymentOption,
  type ReceivableType,
} from '../../api/billing.api';
import { ApiError } from '../../api/transport';
import { MoneyDisplay } from '../../components/MoneyDisplay';
import { useHasPermission } from '../../auth/HasPermission';
import { uploadObjectToCos } from '../../utils/cos-upload';
import { DraggableModal } from '../../components/DraggableModal';

const { Text } = Typography;

interface OfflinePaymentFormValues {
  channel?: OfflinePaymentChannelCode;
  channelEventAt?: Dayjs | null;
  channelTxnNo?: string;
  remark?: string;
}

interface UploadedReceipt {
  objectKey: string;
  filename: string;
}

export interface OfflinePaymentRecordDialogProps {
  open: boolean;
  orderId: string;
  onClose: () => void;
  onRecorded?: () => void;
}

const CHANNEL_OPTIONS: Array<{ value: OfflinePaymentChannelCode; label: string }> = [
  { value: 'WECHAT', label: '微信' },
  { value: 'ALIPAY', label: '支付宝' },
  { value: 'CASH', label: '现金' },
  { value: 'BANK_TRANSFER', label: '银行转账' },
  { value: 'OTHER', label: '其他' },
];

const RECEIPT_ALLOWED_EXTENSIONS = new Set(['.pdf', '.jpg', '.jpeg', '.png']);
const RECEIPT_MAX_SIZE_BYTES = 10 * 1024 * 1024;

function formatTime(value: string | null): string {
  return value ? new Date(value).toLocaleString('zh-CN') : '-';
}

function optionValue(option: OfflinePaymentOption): string {
  return option.receivablePlanId ?? option.target;
}

/** 是否款项形态未定选项（未挂应收节点，登记时锁定形态）。 */
function isUndeterminedOption(option: OfflinePaymentOption): boolean {
  return !option.receivablePlanId;
}

/** 应收节点展示：挂节点取类型；未定形态按本次收款款项选择展示「全款 / 首付款」。 */
function optionPlanLabel(option: OfflinePaymentOption): string {
  if (option.receivablePlanId) {
    // 挂应收节点的选项 target 恒为节点类型（DEPOSIT/FINAL），服务端契约保证
    return RECEIVABLE_TYPE_LABEL[option.target as ReceivableType];
  }
  return option.target === 'FULL' ? '全款' : '首付款';
}

/**
 * 款项选项文案（对齐原型）：未定形态的全款 / 首付款（带尾款说明），挂节点的按节点类型。
 */
function renderOptionLabel(option: OfflinePaymentOption, context: OfflinePaymentContext): React.ReactNode {
  if (isUndeterminedOption(option) && option.target !== 'FULL' && option.followUpPlan) {
    return (
      <>
        首付款 <MoneyDisplay amountInCents={option.payableAmountMinor} />
        （尾款 <MoneyDisplay amountInCents={option.followUpPlan.amountMinor} />
        ，首付款结清后 {context.productSnapshot.finalDueDays} 天内交齐）
      </>
    );
  }
  return (
    <>
      {isUndeterminedOption(option) ? (option.target === 'FULL' ? '一次性全款' : '首付款') : RECEIVABLE_TYPE_LABEL[option.target as ReceivableType]}{' '}
      <MoneyDisplay amountInCents={option.payableAmountMinor} />
    </>
  );
}

/** ADM-09 线下收款登记对话框；金额与登记资格均以服务端上下文为准，UI 对齐原型 OfflinePaymentModal。 */
export function OfflinePaymentRecordDialog({
  open,
  orderId,
  onClose,
  onRecorded,
}: OfflinePaymentRecordDialogProps) {
  const [form] = Form.useForm<OfflinePaymentFormValues>();
  const canRecord = useHasPermission('ADM-09', 'EDIT');
  const [context, setContext] = useState<OfflinePaymentContext | null>(null);
  const [loading, setLoading] = useState(false);
  const [selectedTarget, setSelectedTarget] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const [submitError, setSubmitError] = useState<{ level: 'error' | 'warning'; text: string } | null>(null);
  const [receipt, setReceipt] = useState<UploadedReceipt | null>(null);
  const [receiptUploading, setReceiptUploading] = useState(false);
  const [receiptError, setReceiptError] = useState<string | null>(null);
  const idempotencyKeyRef = useRef<string | null>(null);
  const receiptPreviewUrlRef = useRef<string | null>(null);

  const loadContext = useCallback(async () => {
    setLoading(true);
    try {
      const result = await billingApi.getOfflinePaymentContext(orderId);
      setContext(result);
      setSubmitError(null);
    } catch (err) {
      setContext(null);
      setSubmitError({
        level: 'error',
        text: err instanceof Error ? err.message : '登记上下文加载失败，请稍后重试',
      });
    } finally {
      setLoading(false);
    }
  }, [orderId]);

  useEffect(() => {
    if (!open || !canRecord) return;
    if (!idempotencyKeyRef.current) {
      idempotencyKeyRef.current = crypto.randomUUID();
    }
    void loadContext();
  }, [open, canRecord, loadContext]);

  const releaseReceiptPreview = useCallback(() => {
    const previewUrl = receiptPreviewUrlRef.current;
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
      receiptPreviewUrlRef.current = null;
    }
  }, []);

  useEffect(() => () => releaseReceiptPreview(), [releaseReceiptPreview]);

  useEffect(() => {
    if (!open) {
      releaseReceiptPreview();
      setReceipt(null);
      setReceiptError(null);
      setReceiptUploading(false);
      return;
    }
    idempotencyKeyRef.current = crypto.randomUUID();
    setConfirmed(false);
    setSubmitError(null);
    releaseReceiptPreview();
    setReceipt(null);
    setReceiptError(null);
    setReceiptUploading(false);
    form.resetFields();
  }, [open, form, releaseReceiptPreview]);

  useEffect(() => {
    if (!context?.options.length) {
      setSelectedTarget(null);
      return;
    }
    const current = context.options.find((option) => optionValue(option) === selectedTarget);
    setSelectedTarget(current ? selectedTarget : optionValue(context.options[0]!));
  }, [context, selectedTarget]);

  const selectedOption = useMemo(
    () => context?.options.find((option) => optionValue(option) === selectedTarget) ?? null,
    [context, selectedTarget],
  );

  const handleReceiptUpload = useCallback(async (file: File) => {
    const validationError = validateReceiptFile(file);
    if (validationError) {
      setReceiptError(validationError);
      return;
    }

    setReceiptUploading(true);
    setReceiptError(null);
    try {
      const credential = await billingApi.requestOfflinePaymentReceiptUpload(orderId, {
        filename: file.name,
        fileSizeBytes: file.size,
      });
      await uploadObjectToCos(credential, file);
      setReceipt({ objectKey: credential.objectKey, filename: credential.filename });
    } catch (err) {
      setReceiptError(err instanceof Error ? err.message : '凭证上传失败，请稍后重试');
    } finally {
      setReceiptUploading(false);
    }
  }, [orderId]);

  const handleViewReceipt = useCallback(async () => {
    if (!receipt) return;
    try {
      const relayResult = await billingApi.accessOfflinePaymentReceipt(orderId, {
        objectKey: receipt.objectKey,
      });
      releaseReceiptPreview();
      const previewUrl = URL.createObjectURL(decodeReceiptContent(relayResult));
      receiptPreviewUrlRef.current = previewUrl;
      window.open(previewUrl, '_blank', 'noopener,noreferrer');
    } catch (err) {
      setReceiptError(err instanceof Error ? err.message : '凭证查看失败，请稍后重试');
    }
  }, [orderId, receipt, releaseReceiptPreview]);

  const handleRemoveReceipt = useCallback(() => {
    releaseReceiptPreview();
    setReceipt(null);
    setReceiptError(null);
  }, [releaseReceiptPreview]);

  const handleClose = () => {
    if (submitting) return;
    onClose();
  };

  const handleRegister = useCallback(async () => {
    if (submitting) return;
    try {
      await form.validateFields();
    } catch {
      return;
    }
    if (!confirmed) {
      message.error('请勾选确认到账核对后再登记');
      return;
    }
    await submitOfflinePayment({
      form,
      orderId,
      selectedOption,
      idempotencyKey: idempotencyKeyRef.current,
      receiptObjectKey: receipt?.objectKey ?? null,
      setSubmitting,
      setSubmitError,
      onRecorded,
      onClose,
    });
  }, [form, onClose, onRecorded, orderId, receipt, confirmed, selectedOption, submitting]);

  const hasUndeterminedOptions = Boolean(context?.options.some(isUndeterminedOption));
  const selectedChannel = Form.useWatch('channel', form) ?? 'WECHAT';

  if (!canRecord) {
    return (
      <DraggableModal
        open={open}
        title="线下收款登记"
        onCancel={onClose}
        footer={null}
        data-testid="offline-payment-dialog"
      >
        <Alert
          type="warning"
          showIcon
          message="当前账号无线下收款登记权限"
          description="如需登记线下收款，请联系管理员开通 ADM-09 编辑权限。"
          data-testid="offline-payment-no-permission"
        />
      </DraggableModal>
    );
  }

  return (
    <DraggableModal
      open={open}
      title="线下收款登记"
      width={560}
      onCancel={handleClose}
      closable={!submitting}
      maskClosable={false}
      footer={null}
      data-testid="offline-payment-dialog"
    >
      {loading ? (
        <Spin data-testid="offline-payment-context-loading">
          <div style={{ minHeight: 180 }} />
        </Spin>
      ) : !context ? (
        <Alert
          type={submitError?.level ?? 'error'}
          showIcon
          message={submitError?.text ?? '登记上下文暂不可用'}
          action={(
            <Button size="small" onClick={() => void loadContext()}>
              重试
            </Button>
          )}
        />
      ) : !context.payable ? (
        <Alert
          type="warning"
          showIcon
          message="当前订单不可登记线下收款"
          description={context.notPayableReason ?? '请刷新订单详情后重试'}
        />
      ) : (
        <Space direction="vertical" size={16} style={{ width: '100%' }}>
          {/* 顶部高风险提示（对齐原型文案与位置） */}
          <Alert
            type="warning"
            showIcon
            message="高风险操作：金额由系统按商品定价生成、不可手改，登记后立即影响订单与课时状态，请核对无误后再提交"
            data-testid="offline-payment-risk-alert"
          />
          {submitError && (
            <Alert
              type={submitError.level}
              showIcon
              message={submitError.text}
              action={submitError.level === 'warning' ? (
                <Button size="small" onClick={() => void loadContext()}>
                  刷新登记信息
                </Button>
              ) : undefined}
            />
          )}

          {/* 款项选择：多选项时展示；存在未定形态选项时附确认提示（对齐原型） */}
          {context.options.length > 1 && (
            <div>
              <Text strong>
                {hasUndeterminedOptions ? '本次收款款项（订单款项形态未定，请与家长确认）' : '本次收款款项'}
              </Text>
              <Radio.Group
                style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 8 }}
                value={selectedTarget}
                disabled={submitting}
                onChange={(event) => setSelectedTarget(event.target.value as string)}
                data-testid="offline-payment-target-options"
              >
                {context.options.map((option) => (
                  <Radio key={optionValue(option)} value={optionValue(option)}>
                    {renderOptionLabel(option, context)}
                  </Radio>
                ))}
              </Radio.Group>
            </div>
          )}

          {/* 订单信息（对齐原型字段序；本次固定金额加粗突出） */}
          {selectedOption && (
            <Descriptions bordered size="small" column={2}>
              <Descriptions.Item label="订单号">{context.orderNo}</Descriptions.Item>
              <Descriptions.Item label="学生">{context.student.name}</Descriptions.Item>
              <Descriptions.Item label="商品" span={2}>{context.productSnapshot.name}</Descriptions.Item>
              <Descriptions.Item label="应收节点">{optionPlanLabel(selectedOption)}</Descriptions.Item>
              {selectedOption.receivablePlanId && (
                <>
                  <Descriptions.Item label="节点金额">
                    <MoneyDisplay amountInCents={selectedOption.amountMinor} />
                  </Descriptions.Item>
                  <Descriptions.Item label="已付金额">
                    <MoneyDisplay amountInCents={selectedOption.paidAmountMinor} />
                  </Descriptions.Item>
                  <Descriptions.Item label="到期日">{formatTime(selectedOption.dueAt)}</Descriptions.Item>
                  <Descriptions.Item label="尾款构成">
                    {selectedOption.followUpPlan ? (
                      <MoneyDisplay amountInCents={selectedOption.followUpPlan.amountMinor} />
                    ) : (
                      '-'
                    )}
                  </Descriptions.Item>
                </>
              )}
              <Descriptions.Item label="本次固定金额">
                <Text strong>
                  <MoneyDisplay amountInCents={selectedOption.payableAmountMinor} />
                </Text>
              </Descriptions.Item>
              <Descriptions.Item label="经办人">
                {`${context.currentStaff ? context.currentStaff.name : '-'}（当前登录员工，不可修改）`}
              </Descriptions.Item>
            </Descriptions>
          )}

          <Form
            form={form}
            layout="vertical"
            disabled={submitting}
            initialValues={{ channel: 'WECHAT' as OfflinePaymentChannelCode, channelEventAt: dayjs() }}
          >
            <Form.Item
              name="channel"
              label="收款渠道"
              rules={[
                { required: true, message: '请选择收款渠道' },
                {
                  validator: async (_rule, value: OfflinePaymentChannelCode | undefined) => {
                    if (value && !CHANNEL_OPTIONS.some((option) => option.value === value)) {
                      throw new Error('请选择有效的收款渠道');
                    }
                  },
                },
              ]}
            >
              <Radio.Group>
                {CHANNEL_OPTIONS.map((option) => (
                  <Radio key={option.value} value={option.value}>
                    {option.label}
                  </Radio>
                ))}
              </Radio.Group>
            </Form.Item>
            <Form.Item
              name="channelEventAt"
              label="收款时间"
              rules={[{ required: true, message: '请选择收款时间' }]}
            >
              <DatePicker showTime style={{ width: '100%' }} />
            </Form.Item>
            <Form.Item
              name="channelTxnNo"
              label={selectedChannel === 'CASH' ? '渠道流水号/凭证号（现金可不填）' : '渠道流水号/凭证号'}
              dependencies={['channel']}
              rules={[
                {
                  required: true,
                  validator: async (_rule, value: string | undefined) => {
                    const channel = form.getFieldValue('channel') as OfflinePaymentChannelCode | undefined;
                    if (channel && channel !== 'CASH' && !value?.trim() && !receipt) {
                      throw new Error('非现金渠道请填写渠道流水号或凭证号（或上传收款凭证）');
                    }
                  },
                },
              ]}
            >
              <Input maxLength={128} placeholder="请输入渠道流水号或凭证号" />
            </Form.Item>
            <Form.Item label="收款凭证附件（可选）">
              <Space wrap>
                <Upload
                  accept=".pdf,.jpg,.jpeg,.png"
                  showUploadList={false}
                  disabled={submitting || receiptUploading}
                  customRequest={(options) => {
                    void handleReceiptUpload(options.file as File);
                  }}
                >
                  <Button
                    icon={<UploadOutlined />}
                    loading={receiptUploading}
                    disabled={submitting}
                  >
                    {receipt ? '重新上传凭证' : '上传凭证'}
                  </Button>
                </Upload>
                {receiptUploading && <Tag>上传中</Tag>}
                {receipt && (
                  <>
                    <Tag color="green">已上传</Tag>
                    <Text>{receipt.filename}</Text>
                    <Button
                      size="small"
                      icon={<EyeOutlined />}
                      onClick={() => void handleViewReceipt()}
                      disabled={submitting || receiptUploading}
                    >
                      查看
                    </Button>
                    <Button
                      size="small"
                      danger
                      icon={<DeleteOutlined />}
                      onClick={handleRemoveReceipt}
                      disabled={submitting || receiptUploading}
                    >
                      移除
                    </Button>
                  </>
                )}
              </Space>
              {receiptError && (
                <Text type="danger" style={{ display: 'block', marginTop: 8 }}>
                  {receiptError}
                </Text>
              )}
            </Form.Item>
            <Form.Item name="remark" label="备注">
              <Input.TextArea maxLength={500} rows={2} />
            </Form.Item>
            <Form.Item>
              <Checkbox
                checked={confirmed}
                disabled={submitting}
                onChange={(event) => setConfirmed(event.target.checked)}
                data-testid="offline-payment-confirm-checkbox"
              >
                我已核对渠道到账金额与应收金额一致，确认登记本次收款
              </Checkbox>
            </Form.Item>
          </Form>

          <Space>
            <Button onClick={handleClose} disabled={submitting}>
              取消
            </Button>
            <Button
              type="primary"
              loading={submitting}
              onClick={() => void handleRegister()}
            >
              登记
            </Button>
          </Space>
        </Space>
      )}
    </DraggableModal>
  );
}

interface SubmitOfflinePaymentParams {
  form: ReturnType<typeof Form.useForm<OfflinePaymentFormValues>>[0];
  orderId: string;
  selectedOption: OfflinePaymentOption | null;
  idempotencyKey: string | null;
  receiptObjectKey: string | null;
  setSubmitting: (value: boolean) => void;
  setSubmitError: (value: { level: 'error' | 'warning'; text: string } | null) => void;
  onRecorded?: () => void;
  onClose: () => void;
}

async function submitOfflinePayment(params: SubmitOfflinePaymentParams): Promise<void> {
  const { form, orderId, selectedOption, idempotencyKey, receiptObjectKey } = params;
  if (!selectedOption || !idempotencyKey) return;
  const values = await form.validateFields();
  params.setSubmitting(true);
  try {
    await billingApi.recordOfflinePayment(
      orderId,
      {
        ...(selectedOption.receivablePlanId
          ? { receivablePlanId: selectedOption.receivablePlanId }
          : { intendedPaymentForm: selectedOption.target === 'FULL' ? 'FULL' : 'INSTALLMENT' }),
        channel: values.channel!,
        channelEventAt: values.channelEventAt!.toISOString(),
        channelTxnNo: values.channelTxnNo?.trim() || null,
        receiptObjectKey,
        remark: values.remark?.trim() || null,
      },
      idempotencyKey,
    );
    message.success('登记成功，订单状态已刷新');
    params.onRecorded?.();
    params.onClose();
  } catch (err) {
    params.setSubmitError(buildSubmitError(err));
  } finally {
    params.setSubmitting(false);
  }
}

function validateReceiptFile(file: File): string | null {
  const extension = file.name.slice(file.name.lastIndexOf('.')).toLowerCase();
  if (!RECEIPT_ALLOWED_EXTENSIONS.has(extension)) {
    return '凭证仅支持 PDF、JPG、JPEG 或 PNG 文件';
  }
  if (file.size > RECEIPT_MAX_SIZE_BYTES) {
    return '凭证文件不得超过 10MB';
  }
  return null;
}

function decodeReceiptContent(result: {
  contentType: string;
  contentBase64: string;
}): Blob {
  const binary = window.atob(result.contentBase64);
  const bytes = new Uint8Array(binary.length);
  for (let index = 0; index < binary.length; index += 1) {
    bytes[index] = binary.charCodeAt(index);
  }
  return new Blob([bytes], { type: result.contentType || 'application/octet-stream' });
}

function buildSubmitError(err: unknown): { level: 'error' | 'warning'; text: string } {
  if (!(err instanceof ApiError)) {
    return { level: 'error', text: err instanceof Error ? err.message : '线下收款登记失败，请稍后重试' };
  }
  if (err.status === 401) {
    return { level: 'error', text: '登录已过期，请重新登录后再提交' };
  }
  if (err.status === 403) {
    return { level: 'error', text: '当前账号无线下收款登记权限' };
  }
  if (err.status >= 500) {
    return { level: 'error', text: '服务暂时不可用，请稍后重试' };
  }
  if (err.status === 409 || err.code === 'billing.offline_payment.input_invalid') {
    return { level: 'warning', text: '登记信息已变化或请求冲突，请刷新登记信息后核对再提交' };
  }
  return { level: 'error', text: err.message };
}
